import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import folderIcon from "../../assets/icons/folder.svg";
import subfolderIcon from "../../assets/icons/subfolder.svg";
// import fileIcon from "../../assets/icons/folder_check.svg";
import arrowDownIcon from "../../assets/icons/arrow_right.svg";
import {
  RowType,
  ParentTree,
  AttributesTableProps,
  DestinationNode,
  DestinationRowProps,
} from "./destination-interface";
import dayjs from "dayjs";

function flattenDestinations(
    parents: ParentTree[],
    level: number = 0,
    parentKey: string = ""
): RowType[] {
  const rows: RowType[] = [];
  try {
    parents.forEach((parent, parentIndex) => {
      const parentName = parent.folder1?.trim() || `Folder ${parentIndex + 1}`;
      const parentKeyName = `${parentName}-${parentIndex}`;
      rows.push({
        key: parentKeyName,
        name: parentName,
        authorizedBy: "-",
        authorizedAt: "-",
        version_number: 0,
        country: parentName,
        level: 0,
        isFolder: true,
        isOpen: false,
        children: parent.children
            ? flattenChildren(parent.children, 1, parentKeyName, parent.folder1)
            : [],
      });
    });
  } catch (error) {
    console.error("Error flattening destinations:", error);
  }
  return rows;
}

function flattenChildren(
    nodes: DestinationNode[],
    level: number,
    parentKey: string,
    country: string
): {
  isAgency: boolean;
  country: string;
  level: number;
  authorizedAt: string;
  version_number: number;
  isFolder: boolean;
  isOpen: boolean;
  authorizedBy: string;
  children: RowType[];
  name: string;
  id: any;
  brand: string | undefined;
  key: string;
  account_id: string;
  secret_id: string;
}[] {
  return nodes.map((node, index) => {
    const key = `${parentKey}-${node.name}-${index}`;
    const isFolder = node.type === "folder";

    return {
      key,
      name: node.name,
      authorizedBy: node.authorizedby || "-",
      authorizedAt: node.authorizedat ? dayjs(node.authorizedat).format("MMM DD, YYYY") : "-",
      version_number: node.version_number,
      isAgency: String(node.isAgency) === "1" || false,
      country,
      brand: level === 1 ? node.name : undefined,
      level,
      isFolder,
      isOpen: false,
      id: node?.id,
      account_id: node.account_id || "-",
      secret_id: node.secret_id || "-",
      status_text: node.status_text || "-",
      children: node.children?.length
          ? flattenChildren(node.children, level + 1, key, country)
          : [],
    };
  });
}

function DestinationRow({ row, isOpen, toggleOpen }: DestinationRowProps) {
  const indent = row.level * 24;

  const handleRowClick = () => {
    if (row.isFolder) {
      toggleOpen(row.key);
    }
  };

  let icon = null;
  if (row.isFolder || row.level === 0) {
    icon =
      row.level === 0 ? (
        <img src={folderIcon} alt="Folder" style={{ width: 20, height: 20, marginRight: 8 }} />
      ) : (
        <img src={subfolderIcon} alt="Subfolder" style={{ width: 20, height: 20, marginRight: 8 }}/>
      );
  }

  const arrowIcon = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        toggleOpen(row.key);
      }}
      sx={{ p: 0.5, mr: 0.5 }}
    >
      <img
        src={arrowDownIcon}
        alt={isOpen ? "Collapse" : "Expand"}
        style={{
          width: 16,
          height: 16,
          transform: isOpen ? "rotate(0deg)" : "rotate(270deg)",
          transition: "transform 0.2s",
        }}
      />
    </IconButton>
  );

  return (
    <TableRow hover onClick={handleRowClick} style={{ cursor: "pointer" }}>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", pl: `${indent}px` }}>
          {row.isFolder ? arrowIcon : <Box sx={{ width: 32 }} />}
          {icon}
          <Typography
            variant="h6"
            sx={{
              fontSize: "calc(14 / var(--base-font) * 1rem)",
              fontWeight: row.level === 0 ? 700 : row.level === 3 ? 500 : 400,
              color: row.level === 0 ? "#e6007a" : "#222",
            }}
          >
            {row.name ? row.name : "-"}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {row.isFolder ? "-" : row.authorizedBy}
        </Box>
      </TableCell>
      <TableCell>{row.isFolder ? "-" : row.authorizedAt}</TableCell>
      <TableCell
        sx={{
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.isFolder ? "-" : row.isAgency === true ? "Yes" : "No"}
      </TableCell>
      <TableCell>{row.isFolder ? "-" : row.version_number}</TableCell>
      <TableCell>{row.isFolder ? "-" : row.account_id}</TableCell>
      <TableCell>{row.isFolder ? "-" : row.secret_id}</TableCell>
      <TableCell>{row.isFolder ? "-" : row.status_text}</TableCell>
    </TableRow>
  );
}

function toggleIsOpen(rows: RowType[], key: string): RowType[] {
  return rows.map((row) => {
    if (row.key === key) {
      return { ...row, isOpen: !row.isOpen };
    }
    if (row.children) {
      return { ...row, children: toggleIsOpen(row.children, key) };
    }
    return row;
  });
}

function DestinationsTableTree({ data, stickyTop}: AttributesTableProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));

  const getColumnWidths = () => {
    if (isXs) {
      return {
        name: "30%",
        authorized_by: "12%",
        authorized_at: "9%",
        agency: "7%",
        version: "8%",
        account_id: "8%",
        secret_id: "14%",
        status: "12%"
      };
    }
    if (isSm) {
      return {
        name: "30%",
        authorized_by: "12%",
        authorized_at: "9%",
        agency: "7%",
        version: "8%",
        account_id: "8%",
        secret_id: "14%",
        status: "12%"
      };
    }
    if (isMd) {
      return {
        name: "30%",
        authorized_by: "12%",
        authorized_at: "9%",
        agency: "7%",
        version: "8%",
        account_id: "8%",
        secret_id: "14%",
        status: "12%"
      };
    }
    if (isLg) {
      return {
        name: "30%",
        authorized_by: "12%",
        authorized_at: "9%",
        agency: "7%",
        version: "8%",
        account_id: "8%",
        secret_id: "14%",
        status: "12%"
      };
    }
    return {
      name: "30%",
      authorized_by: "12%",
      authorized_at: "9%",
      agency: "7%",
      version: "8%",
      account_id: "8%",
      secret_id: "14%",
      status: "12%"
    };
  };

  const colWidths = getColumnWidths();

  const [rows, setRows] = useState<RowType[]>([]);

  const [allExpanded, setAllExpanded] = useState(false);

  function setAllRowsOpen(rows: RowType[], open: boolean): RowType[] {
    return rows.map((row) => ({
      ...row,
      isOpen: open,
      children: row.children ? setAllRowsOpen(row.children, open) : [],
    }));
  }

  const arrowIconHeader = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        const newState = !allExpanded;
        setAllExpanded(newState);

        // Update openMap for consistency
        const updatedMap: Record<string, boolean> = {};
        const updateMap = (rows: RowType[]) => {
          rows.forEach((row) => {
            if (row.children && row.children.length) {
              updatedMap[row.key] = newState;
              updateMap(row.children);
            }
          });
        };
        updateMap(rows);

        setOpenMap(updatedMap);
        setRows((prevRows) => setAllRowsOpen(prevRows, newState));
      }}
      sx={{ p: 0.5, mr: 0.5 }}
    >
      <img
        src={arrowDownIcon}
        alt={allExpanded ? "Collapse all" : "Expand all"}
        style={{
          width: 16,
          height: 16,
          transform: allExpanded ? "rotate(0deg)" : "rotate(270deg)",
          transition: "transform 0.2s",
        }}
      />
    </IconButton>
  );

  useEffect(() => {
    if (Array.isArray(data)) {
      setRows(flattenDestinations(data as ParentTree[]));
    }
  }, [data]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const toggleOpen = (key: string) => {
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
    setRows((prevRows) => toggleIsOpen(prevRows, key));
  };

  const renderRows = (rows: RowType[]): React.ReactElement[] => {
    return rows.flatMap((row) => {
      const rowElement = (
        <DestinationRow
          key={row.key}
          isOpen={!!openMap[row.key]}
          row={row}
          toggleOpen={toggleOpen}
        />
      );
      if (row.isFolder && openMap[row.key] && row.children) {
        return [rowElement, ...renderRows(row.children)];
      }
      return [rowElement];
    });
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead sx={{ "--table-sticky-top": `${stickyTop}px` } as React.CSSProperties}>
          <TableRow>
            <TableCell className="cell-name" sx={{ width: colWidths.name, minWidth: 80 }}>
              {arrowIconHeader} Name
            </TableCell>
            <TableCell className="cell-authorized_by" sx={{ width: colWidths.authorized_by, minWidth: 20 }}>
              Authorized by
            </TableCell>
            <TableCell className="cell-authorized_at" sx={{ width: colWidths.authorized_at, minWidth: 10 }}>
              Authorized at
            </TableCell>
            <TableCell className="cell-agency" sx={{ width: colWidths.agency, minWidth: 10 }}>
              Agency
            </TableCell>
            <TableCell className="cell-version" sx={{ width: colWidths.version, minWidth: 10 }}>
              Version number
            </TableCell>
            <TableCell className="cell-accountid" sx={{ width: colWidths.account_id, minWidth: 20 }}>
              Account ID
            </TableCell>
            <TableCell className="cell-secretid" sx={{ width: colWidths.secret_id, minWidth: 20 }}>
              Secret ID
            </TableCell>
            <TableCell className="cell-status" sx={{ width: colWidths.status, minWidth: 20 }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows(rows)}</TableBody>
      </Table>
    </TableContainer>
  );
}

export default DestinationsTableTree;
